import { DwhSriCategory } from '@ndvr/market-data-api';
import { PortfolioTradePreferences, RestrictionType, TradeRestrictionAsset } from '@ndvr/portfolio-api';

import { AssetInfo, Position } from '@/cip/_openapi/portfolio-optimizer';
import { restrictionTypeMap } from 'views/portfolio/PortfolioPage/tabs/equity-preferences/constants';

interface RestrictionRow {
    key: string;
    label: string;
    details: string;
    position?: number;
}

export type PersonalizedIndexingRow = RestrictionRow &
    (
        | { kind: 'category'; categoryId: string; type: 'SRI Screen' }
        | {
              kind: 'security';
              symbol?: string;
              type: string;
              restrictionType: RestrictionType;
              /** Unknown when membership or a share-class ID is unavailable. */
              inUniverse?: boolean;
          }
    );

const stocksLabel = (count: number) => `${count} ${count === 1 ? 'stock' : 'stocks'}`;
const heldExposure = (positions: Position[], matches: (position: Position) => boolean) =>
    positions.filter(matches).reduce((sum, { base }) => sum + base.netExposure, 0);
const byLabel = (a: PersonalizedIndexingRow, b: PersonalizedIndexingRow) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

/** Rolls subcategories into sorted SRI category rows, then sorted individual client restrictions. */
export const buildPersonalizedIndexingRows = (
    categories: DwhSriCategory[],
    preferences: Pick<
        PortfolioTradePreferences,
        'restrictedCategoryIds' | 'restrictedCategoryOverrides' | 'restrictions'
    >,
    assets: AssetInfo[],
    positions: Position[] | undefined,
    universeShareClassIds?: ReadonlySet<string>,
): PersonalizedIndexingRow[] => {
    const restrictedIds = new Set(preferences.restrictedCategoryIds);
    const keptShareClassIds = new Set(preferences.restrictedCategoryOverrides.map(({ shareClassId }) => shareClassId));
    const assetsByShareId = new Map(assets.map((asset) => [asset.sId, asset]));
    const positionsByShareId = new Map((positions ?? []).map(({ base }) => [base.sId, base]));
    const resolvedShareClassId = (restriction: TradeRestrictionAsset) =>
        assetsByShareId.get(restriction.shareId)?.scId ??
        restriction.shareClassId ??
        positionsByShareId.get(restriction.shareId)?.scId;
    // Report client-restricted stocks only in their own rows, even when the saved share class is absent.
    const clientShareClassIds = new Set(preferences.restrictions.flatMap((item) => resolvedShareClassId(item) ?? []));
    const clientShareIds = new Set(preferences.restrictions.map(({ shareId }) => shareId));

    const categoryRows = categories.flatMap((category): PersonalizedIndexingRow[] => {
        const selected = category.subCategories.filter(({ sriSubCategoryId }) => restrictedIds.has(sriSubCategoryId));
        if (!selected.length) return [];
        const shareClassIds = new Set(selected.flatMap(({ scIds }) => scIds));
        const removed = new Set<string>();
        let kept = 0;
        for (const id of shareClassIds) {
            if (clientShareClassIds.has(id)) continue;
            if (keptShareClassIds.has(id)) kept++;
            else removed.add(id);
        }
        return [
            {
                key: `category:${category.sriCategoryId}`,
                kind: 'category',
                label: category.sriCategoryLabel ?? category.sriCategoryId,
                categoryId: category.sriCategoryId,
                type: 'SRI Screen',
                details: `${stocksLabel(removed.size)} removed${kept ? ` · ${kept} kept` : ''}`,
                position:
                    positions &&
                    heldExposure(
                        positions,
                        ({ base }) => !!base.scId && removed.has(base.scId) && !clientShareIds.has(base.sId),
                    ),
            },
        ];
    });

    const securityRows = preferences.restrictions.map(
        (restriction): Extract<PersonalizedIndexingRow, { kind: 'security' }> => {
            const asset = assetsByShareId.get(restriction.shareId);
            const shareClassId = resolvedShareClassId(restriction);
            return {
                key: `security:${restriction.shareId}`,
                kind: 'security',
                label: asset?.symbol || asset?.description || restriction.shareId,
                symbol: asset?.symbol,
                inUniverse: shareClassId && universeShareClassIds ? universeShareClassIds.has(shareClassId) : undefined,
                type: restrictionTypeMap[restriction.restrictionType],
                restrictionType: restriction.restrictionType,
                details: '—',
                position:
                    positions &&
                    heldExposure(
                        positions,
                        ({ base }) =>
                            base.sId === restriction.shareId ||
                            (!!base.scId && !!shareClassId && base.scId === shareClassId),
                    ),
            };
        },
    );

    categoryRows.sort(byLabel);
    securityRows.sort((a, b) => Number(a.inUniverse === false) - Number(b.inUniverse === false) || byLabel(a, b));
    return [...categoryRows, ...securityRows];
};
